import { apiClient, getData, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import type { ConfirmRequest } from '@/features/payments/api/adapter'
import type { Gateway } from '@/schemas/payments'
import {
  checkoutStartSchema,
  discountPreviewSchema,
  entitlementSchema,
  paymentRecordListSchema,
  planDetailSchema,
  planListSchema,
  purchaseResultSchema,
  type CheckoutStart,
  type DiscountPreview,
  type Entitlement,
  type FeatureMap,
  type PaymentRecord,
  type Plan,
  type PlanDetail,
  type PurchaseResult,
} from '@/schemas/subscriptions'

/**
 * The subscriptions seam: mm-be's `/api/v5/subscriptions/` in, app types out.
 *
 * Structured like `features/courses/api/courseService.ts`: wire types come
 * from `@/api/schema`, never restated, so a regenerated `schema.d.ts` that
 * renames a field breaks the build HERE; every mapper's result goes through
 * its Zod schema before it leaves the module.
 *
 * Not-found answers are NOT handled here — every read throws its ApiError, and
 * `queries.ts` decides which codes mean "nothing there".
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

const TIER_FREE = 'free'

// ── Mappers ──────────────────────────────────────────────────────────────────
//
// Exported so each one is testable on a fixture without a request. They map
// only; the schema at the call site checks what they produce.

/** `title`, `name`, `description` and the legacy labels are dropped — schemas/subscriptions.ts rule 1. */
export const mapPlan = (wire: Wire<'Plan'>) => ({
  id: wire.id,
  tier: wire.tier,
  price: {
    amount: wire.price.amount,
    vat: wire.price.vat,
    total: wire.price.total,
    was: wire.price.was,
  },
  periodDays: wire.period_days,
  isFree: wire.is_free,
  isTrial: wire.is_trial,
  appOnly: wire.app_only,
  allowSignal: wire.allow_signal,
  displayOrder: wire.display_order,
})

/** Drops each entry's `key`, which only repeats the map key. */
export const mapFeatures = (wire: Record<string, Wire<'FeatureLimit'>>): FeatureMap =>
  Object.fromEntries(
    Object.entries(wire).map(([key, flag]) => [key, { enabled: flag.enabled, limit: flag.limit }]),
  )

/**
 * `signals` lives on the plan row (`allow_signal`), not in its feature row,
 * and mm-be folds it into `me/`'s map only. Folded the same way here
 * (`entitlement._with_plan_level_features`), or a with-signals plan's list
 * could never name the one thing it adds over its twin.
 */
export const withPlanLevelFeatures = (
  plan: { allowSignal: boolean },
  features: FeatureMap,
): FeatureMap => ({
  ...features,
  signals: { enabled: plan.allowSignal, limit: plan.allowSignal ? null : 0 },
})

export const mapEntitlement = (wire: Wire<'Entitlement'>) => ({
  tier: wire.tier,
  planId: wire.plan_id,
  planTitle: wire.plan_title,
  activeUntil: wire.tier === TIER_FREE ? null : wire.expires_on,
  features: mapFeatures(wire.features),
  enforced: wire.enforced,
})

export const mapPayment = (wire: Wire<'PaymentRecord'>) => ({
  transactionId: wire.transaction_id,
  planId: wire.plan_id,
  planTitle: wire.plan_title,
  amount: wire.amount,
  method: wire.method,
  status: wire.status,
  startedOn: wire.started_on,
  completedOn: wire.completed_on,
})

const mapDiscount = (wire: Wire<'Discount'>) => ({
  kind: wire.kind,
  code: wire.code,
  amountOff: wire.amount_off,
  payable: wire.payable,
})

/**
 * `fields` defaults to `{}`: always on the wire, but optional in the OpenAPI.
 * A method this build does not know fails the schema — there is no safe way
 * to hand off to it.
 */
export const mapCheckoutStart = (wire: Wire<'CheckoutStartResponse'>) => ({
  transactionId: wire.transaction_id,
  amount: wire.amount,
  gateway: wire.gateway,
  handoff:
    wire.params.method === 'form_post'
      ? { method: wire.params.method, url: wire.params.url, fields: wire.params.fields ?? {} }
      : { method: wire.params.method, url: wire.params.url },
  discount: wire.discount === null ? null : mapDiscount(wire.discount),
})

export const mapPurchaseResult = (wire: Wire<'PurchaseResult'>) => ({
  transactionId: wire.transaction_id,
  planId: wire.plan_id,
  planTitle: wire.plan_title,
  activeUntil: wire.expires_on,
  alreadyCompleted: wire.already_completed,
})

/** A code of only whitespace is sent as no code — mm-be would refuse it. */
const trimmedCode = (code: string | undefined): string | undefined => {
  const trimmed = code?.trim()
  return trimmed === undefined || trimmed === '' ? undefined : trimmed
}

export interface CheckoutRequest {
  planId: number
  gateway: Gateway
  code?: string | undefined
}

export const subscriptionService = {
  /**
   * PUBLIC. The purchasable catalogue, server-ordered (`display_order`, id),
   * at most 50. No Free row, but app-only and trial rows are included —
   * `lib/catalog.ts` decides what the web shows.
   */
  plans: async (): Promise<Plan[]> => {
    const { rows } = await getPage<Wire<'Plan'>>(() =>
      apiClient.GET('/api/v5/subscriptions/plans/'),
    )
    return planListSchema.parse(rows.map(mapPlan))
  },

  /**
   * PUBLIC. Any active plan — Free and app-only included — with its feature
   * flags, `signals` folded in as `me/` has it. 404 `PLAN_NOT_FOUND` for an
   * unknown or retired id.
   */
  plan: async (planId: number): Promise<PlanDetail> => {
    const data = await getData<Wire<'PlanDetail'>>(() =>
      apiClient.GET('/api/v5/subscriptions/plans/{plan_id}/', {
        params: { path: { plan_id: planId } },
      }),
    )
    const plan = mapPlan(data.plan)
    return planDetailSchema.parse({
      plan,
      features: withPlanLevelFeatures(plan, mapFeatures(data.features)),
    })
  },

  me: async (): Promise<Entitlement> => {
    const data = await getData<Wire<'Entitlement'>>(() =>
      apiClient.GET('/api/v5/subscriptions/me/'),
    )
    return entitlementSchema.parse(mapEntitlement(data))
  },

  /** Newest first, at most 100 rows, abandoned attempts included. */
  payments: async (): Promise<PaymentRecord[]> => {
    const { rows } = await getPage<Wire<'PaymentRecord'>>(() =>
      apiClient.GET('/api/v5/subscriptions/payments/'),
    )
    return paymentRecordListSchema.parse(rows.map(mapPayment))
  },

  /**
   * Writes nothing. 400 `DISCOUNT_NOT_APPLICABLE` for every kind of bad code
   * (one code on purpose); 429 `RATE_LIMITED` past 30 an hour.
   */
  previewDiscount: async (planId: number, code: string): Promise<DiscountPreview> => {
    const data = await getData<Wire<'DiscountPreviewResponse'>>(() =>
      apiClient.POST('/api/v5/subscriptions/discount/', {
        body: { plan_id: planId, code: code.trim() },
      }),
    )
    return discountPreviewSchema.parse({
      planId: data.plan_id,
      listPrice: data.list_price,
      discount: mapDiscount(data.discount),
    })
  },

  /**
   * WRITES a Pending payment row before it answers, so every call is a new
   * attempt — call it once per explicit Pay. 429 `RATE_LIMITED` past 20 an
   * hour; 503 `PAYMENT_GATEWAY_UNAVAILABLE` when that gateway is not set up on
   * this deploy (the only signal there is).
   */
  startCheckout: async ({ planId, gateway, code }: CheckoutRequest): Promise<CheckoutStart> => {
    const sentCode = trimmedCode(code)
    const data = await getData<Wire<'CheckoutStartResponse'>>(() =>
      apiClient.POST('/api/v5/subscriptions/checkout/', {
        body: { plan_id: planId, gateway, ...(sentCode === undefined ? {} : { code: sentCode }) },
      }),
    )
    return checkoutStartSchema.parse(mapCheckoutStart(data))
  },

  /** Idempotent: an attempt already granted answers 200 `already_completed`. */
  confirmCheckout: async ({
    transactionId,
    esewaData,
  }: ConfirmRequest): Promise<PurchaseResult> => {
    const data = await getData<Wire<'PurchaseResult'>>(() =>
      apiClient.POST('/api/v5/subscriptions/checkout/confirm/', {
        body: {
          transaction_id: transactionId,
          ...(esewaData === undefined || esewaData === null ? {} : { esewa_data: esewaData }),
        },
      }),
    )
    return purchaseResultSchema.parse(mapPurchaseResult(data))
  },
}
